// ProductsStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from "@/stores/UserStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useEventStore } from "@/stores/EventsStore"
import { useModalStore } from "@/stores/ModalStore"
import { abortRequestsGetThatStartsWith, isBackendRequestPending } from "@/utils/backend"
import debounce from 'lodash/debounce'

export const useProductsStore = defineStore('productsStore', () => {
  const { t } = useI18n()
  const useStore = useUserStore()
  const calendarStore = useCalendarStore()
  const eventStore = useEventStore()
  const modalStore = useModalStore()

  // State
  const currentProduct = ref(null)
  const searchedProduct = ref('') // Holds the search term
  const isLoading = ref(false)

  const selectedProductType = ref(1) // default sale

  const selectedSeller = computed({
    get: () => {

      // Only for sales
      if(modalStore.modals.calendarSales) {

        // How many employees are selected in filters
        const employeeCalendarIds = (calendarStore.calendarEmployeesCalendars || []).map(c => c.id)
        const selectedCalendarIds = (calendarStore.selectedCalendars || [])
        //const selectedEmployeeIds = selectedCalendarIds.filter(id => employeeCalendarIds.includes(id))
        const selectedEmployeeIds = employeeCalendarIds.filter(id => selectedCalendarIds.includes(id))

        // If exactly one employee is selected - return it
        if (selectedEmployeeIds.length === 1) {
          const singleId = selectedEmployeeIds[0]

          return calendarStore.availableCalendars.find((obj) => obj.id === singleId) || null
        }

        // Else return currently logged user
        const currentlyLoggedEmployee = calendarStore.availableCalendars.find((obj) => obj.id === useStore.currentUser.ekey)
        if(currentlyLoggedEmployee) return currentlyLoggedEmployee
      }

      return eventStore.selectedEmployee
    },

    set: (value) => {
      if(value === null) eventStore.selectedEvent.employeeId = null
      else {
        if(typeof value === "string" ) {
          eventStore.selectedEvent.employeeId = value
        }
        if(typeof value === "object") {
          eventStore.selectedEvent.employeeId = value.id
        }
      }
    },
  })

  // Local changes
  const fetchedProducts = ref([]) //  // Data fetched from the API

  // 👉 Product Calculation Methods
  // 👉 Calculate the raw value of a product (price * quantity)
  const calculateProductValue = (price, quantity) => {
    return Number((Number(price) * Number(quantity)).toFixed(2))
  }

  // 👉 Calculate the final price after applying discounts
  const calculateProductPrice = (price, quantity, discountType, discountValue) => {
    const totalBasePrice = Number(price) * Number(quantity)

    // Apply discount based on type to the total price
    if (discountType === 'solid' && discountValue) {
      return Number(Math.max(totalBasePrice - Number(discountValue), 0).toFixed(2))
    } else if (discountType === 'percent' && discountValue) {
      return Number(Math.max(totalBasePrice * (1 - (Number(discountValue) / 100)), 0).toFixed(2))
    }

    return totalBasePrice
  }

  // 👉 Calculate discount amount based on discount type
  const calculateProductDiscountAmount = (totalValue, discountType, discountValue) => {
    if (discountType === 'solid' && discountValue) {
      return Number(discountValue)
    } else if (discountType === 'percent' && discountValue) {
      return Number( ((totalValue * Number(discountValue)) / 100).toFixed(2) )
    }
    // If no discount, return 0

    return 0
  }

  // 👉 Update all price-related fields for a product
  const updateProductPricing = (product) => {
    if (!product) return product

    const price = Number(product.price) || 0
    const quantity = Number(product.quantity) || 1

    // Calculate raw value
    const totalValue = calculateProductValue(price, quantity)

    // Special handling for percentage discounts to ensure they're calculated properly
    if (product.discountType === 'percent' && product.discountValue) {
      // For percentage discounts, ensure we're working with the latest price
      // and calculating the discount based on the current total value
      const discountPercent = Number(product.discountValue)
      const discountAmount = Number(((totalValue * discountPercent) / 100).toFixed(2))
      const finalPrice = Number(Math.max(totalValue - discountAmount, 0).toFixed(2))

      // Update all values
      product.price = price
      product.quantity = quantity
      //product.value = totalValue B: not neded
      product.finalPrice = finalPrice
      product.discount = discountAmount

      return product
    }

    // For non-percentage discounts, use the standard calculation
    const finalPrice = calculateProductPrice(
      price,
      quantity,
      product.discountType,
      product.discountValue,
    )

    // Calculate discount amount
    const discountAmount = calculateProductDiscountAmount(
      totalValue,
      product.discountType,
      product.discountValue,
    )

    // Mutate the original product directly
    product.price = price
    product.quantity = quantity
    //product.value = totalValue
    product.finalPrice = finalPrice
    product.discount = discountAmount

    return product
  }

  const setCurrentProduct = (product) => {
    currentProduct.value = product
  }


  // const updateProductsArray = (searchTerm) => {
  //   // Get all fetched products and local products
  //   let result = [...fetchedProducts.value, ...newProducts.value]
  //
  //
  // // const updateProductsArray = (searchTerm) => {
  // //   const products = Array.isArray(fetchedProducts.value) ? fetchedProducts.value : Object.values(fetchedProducts.value)
  // //   // Get all fetched products and local products
  // //   let result = [...products]
  // //
  // //   // Add "not found" item if no matches
  // //   if (result.length === 0 && searchTerm && searchTerm.length > 2) {
  // //     result.push({
  // //       id: 0,
  // //       salonerId: "0", // Fixed ESLint camelcase warning
  // //       title: t('productNotFound'),
  // //       search: searchTerm,
  // //       category: "",
  // //       type: "",
  // //       price: "",
  // //       // other properties remain the same
  // //       externalId: "", // Fixed ESLint camelcase warning
  // //       _tab: "salonerstore",
  // //     })
  // //   }
  // //
  // //   //productsArray.value = result
  // // }
  //   // Add "not found" item if no matches
  //   if (result.length === 0 && searchTerm && searchTerm.length > 2) {
  //     result.push({
  //       salonerId: "0", // Fixed ESLint camelcase warning
  //       title: t('productNotFound'),
  //       search: searchTerm,
  //       category: "",
  //       type: "",
  //       price: "",
  //       // other properties remain the same
  //       externalId: "", // Fixed ESLint camelcase warning
  //       _tab: "salonerstore",
  //     })
  //   }
  //
  //   productsArray.value = result
  // }

  // const clearSearchedProduct = () => searchedProduct.value = ''
  // const clearCurrentProduct = () => currentProduct.value = null

  // Getters

  // Actions
  const fetchProducts = debounce(async(searchTerm = '') => {
    //fetchedProducts.value = [] //B: albo zostawiamy ze ma byc wybrany 1wszy item z listy albo czyscimy za kazdym razem liste
    // First check if search term is valid
    if (!searchTerm || searchTerm.length < 1) {
      //updateProductsArray(searchTerm)
      abortRequestsGetThatStartsWith('/calendar/products')
      isLoading.value = false
      fetchedProducts.value = []

      return
    }

    // Save the search term and set loading state
    searchedProduct.value = searchTerm.trim()
    isLoading.value = true

    // default for BE is 'finalize'
    const mode = selectedProductType.value === 1 ? 'product-selling' : 'finalize'

    try {

      const response = await $backend(`/calendar/products`, {
        method: 'POST',
        body: JSON.stringify({ term: searchTerm, mode: mode }),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.success) {
        fetchedProducts.value = Array.isArray(response.data) ? response.data : Object.values(response.data)
      }


      if(fetchedProducts.value.length === 0 || fetchedProducts.value.filter( product => product.type === eventStore.selectedProductType || product.type === 3 ).length === 0) {
        fetchedProducts.value.push({
          notFound: true,
          id: 0,
          title: t('productNotFound'),
          search: searchTerm,
          category: "",
          price: "",
          type: 3,
        })
      }



      //console.log('MY PRODUCTS:', fetchedProducts.value)
      isLoading.value = false


    } catch (err) {
      // B: ITS NEEDED HERE
    } finally {
      //console.log('isBackendRequestPending', isBackendRequestPending('/calendar/products'))
      //updateProductsArray(searchTerm)
    }
  }, 150)


  return {
    // State
    fetchedProducts,
    currentProduct,
    searchedProduct,
    selectedSeller,
    selectedProductType,
    isLoading,

    // Calculation methods
    calculateProductValue,
    calculateProductPrice,
    calculateProductDiscountAmount,
    updateProductPricing,
    // Actions
    fetchProducts,
    setCurrentProduct,

    // Getters

  }
})
